/* contactLinks.js — pure helpers for deep-link communication.
 *
 * No UI, no components. Just URL builders + a phone sanitizer.
 * WhatsApp's `wa.me` only accepts digits-only international format,
 * so we have to normalise UK local numbers (07…) to E.164 (+447…).
 *
 * Failure mode: any builder returns `null` when its input is missing
 * or unrecoverable. Callers must check for `null` and either hide the
 * action or render it disabled — never link to an empty href.
 */

/** Sanitize a free-text phone into digits-only, with UK 07… → 447… normalisation. */
export const sanitizePhone = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  let digits = s.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) digits = digits.slice(1);
  // UK local mobile: 07XXXXXXXXX → 447XXXXXXXXX
  if (/^0\d{10}$/.test(digits) && digits.startsWith("07")) {
    digits = `44${digits.slice(1)}`;
  }
  // Reject obviously bad numbers (too short to be a real phone).
  if (digits.length < 8) return null;
  return digits;
};

/** Format a phone for human display — preserves whatever the user typed. */
export const displayPhone = (raw) => (raw ? String(raw).trim() : "");

/** `tel:` link. Returns null when no usable phone. */
export const telHref = (raw) => {
  const d = sanitizePhone(raw);
  return d ? `tel:+${d}` : null;
};

/** `mailto:` link. Returns null when missing or obviously malformed. */
export const mailtoHref = (email) => {
  if (!email) return null;
  const s = String(email).trim();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s)) return null;
  return `mailto:${s}`;
};

/** WhatsApp deep-link. Pre-fills `text` when provided. */
export const whatsappHref = (raw, text) => {
  const d = sanitizePhone(raw);
  if (!d) return null;
  const base = `https://wa.me/${d}`;
  if (!text) return base;
  return `${base}?text=${encodeURIComponent(text)}`;
};

/**
 * Operational chase template — calm, transaction-contextual.
 * First name omitted when we only have a role label ("Buyer", "Solicitor").
 */
export const whatsappChaseTemplate = ({ firstName, propertyAddress, itemTitle, daysWaiting }) => {
  const ROLE_TOKENS = new Set([
    "Buyer", "Seller", "Solicitor", "Broker", "Agent",
    "Participant", "Mortgage", "Lender", "Managing",
  ]);
  const cleanFirst = (firstName || "").trim().split(" ")[0];
  const greet = cleanFirst && !ROLE_TOKENS.has(cleanFirst) ? `Hi ${cleanFirst},` : "Hi,";
  const addrPart  = propertyAddress ? ` for ${propertyAddress}` : "";
  const itemPart  = itemTitle ? ` regarding ${itemTitle}` : "";
  const daysPart =
    daysWaiting && daysWaiting > 0
      ? ` It's been ${daysWaiting} day${daysWaiting === 1 ? "" : "s"} since we last heard.`
      : "";
  return (
    `${greet} quick update from the Deal Room${addrPart}${itemPart}.${daysPart}\n\n` +
    `Any timing you can share would help us keep the transaction moving. Thanks!`
  );
};

/**
 * Operational intervention: log a chase + open the WhatsApp thread.
 *
 * Calls the existing `/api/deals/{id}/awaiting/chase` endpoint with
 * `channel: "whatsapp"` and `send_email: false`, then opens
 * `wa.me/{sanitised}?text=…` in a new tab. Returns `false` when the
 * phone number isn't usable so the caller can surface a calm toast.
 */
export const launchWhatsappChase = async ({
  api,
  dealId,
  phone,
  firstName,
  propertyAddress,
  blocker,           // optional { id, title, owner_role, age_days }
}) => {
  const body = whatsappChaseTemplate({
    firstName,
    propertyAddress,
    itemTitle: blocker?.title,
    daysWaiting: blocker?.age_days || blocker?.days_outstanding || 0,
  });
  const href = whatsappHref(phone, body);
  if (!href) return { ok: false, reason: "no_phone" };

  // Open the WhatsApp tab *before* the await so iOS/Safari treats it
  // as a direct user-initiated navigation (popup-blocker friendly).
  // We use window.open with a sentinel and update href after — but
  // since the URL is known immediately, this is simpler.
  const win = window.open(href, "_blank", "noopener,noreferrer");

  try {
    await api.post(`/api/deals/${dealId}/awaiting/chase`, {
      item_id:      blocker?.id || "whatsapp_chase",
      title:        blocker?.title || "WhatsApp follow-up",
      owner:        blocker?.owner_role || null,
      days_waiting: blocker?.age_days || blocker?.days_outstanding || 0,
      send_email:   false,
      channel:      "whatsapp",
      note:         body,
    });
    return { ok: true, href };
  } catch (err) {
    // Activity log failed but the WhatsApp tab is open — operator can
    // still send. Surface the error so the caller can toast.
    if (win) { /* keep the tab; user is mid-conversation */ }
    return { ok: false, reason: "log_failed", error: err, href };
  }
};


/* ─── Intervention summary ───────────────────────────────────────────
 *
 * Read-only derivation over the existing `deals.activity[]` chase rows
 * (which now persist `channel` + `at` + `ref_owner`). Used to surface
 * a tiny "WhatsApp chase · 2d ago" line under participant names so
 * agents can see at a glance whether the person has already been
 * chased — no new backend, no new state.
 */

const _ROLE_BUCKETS = [
  // longest patterns first so "buyer solicitor" matches before "buyer".
  { keys: ["buyer solicitor", "buyer_solicitor", "buyer-solicitor"], bucket: "buyer_solicitor" },
  { keys: ["seller solicitor", "seller_solicitor", "seller-solicitor"], bucket: "seller_solicitor" },
  { keys: ["solicitor"], bucket: "solicitor" },
  { keys: ["mortgage broker", "broker", "mortgage"], bucket: "broker" },
  { keys: ["estate agent", "agent"], bucket: "agent" },
  { keys: ["surveyor"], bucket: "surveyor" },
  { keys: ["buyer"], bucket: "buyer" },
  { keys: ["seller", "vendor"], bucket: "seller" },
];

/** Map any free-text role label to a canonical bucket id. */
export const roleBucket = (label) => {
  const r = String(label || "").toLowerCase().trim();
  if (!r) return null;
  for (const { keys, bucket } of _ROLE_BUCKETS) {
    if (keys.some((k) => r.includes(k))) return bucket;
  }
  return null;
};

/**
 * Find the most recent intervention against a given role bucket.
 *
 * @param  {Array}  activity  — `deal.activity[]` rows from the Mongo
 *                              deal document; each row carries
 *                              `{kind, channel, ref_owner, at}`.
 * @param  {string} roleLabel — free-text role ("buyer", "buyer solicitor").
 * @return {object|null}      — `{channel, at, daysAgo, label}` or null.
 */
export const lastInterventionFor = (activity, roleLabel) => {
  if (!Array.isArray(activity) || !roleLabel) return null;
  const target = roleBucket(roleLabel);
  if (!target) return null;

  // Most recent chase row whose ref_owner resolves to the same bucket.
  for (const row of activity) {
    if (!row || row.kind !== "chase") continue;
    const refBucket = roleBucket(row.ref_owner);
    if (refBucket !== target) continue;
    const channel = (row.channel || "email").toLowerCase();
    const at      = row.at || row.created_at || null;
    if (!at) continue;
    const ts = new Date(at).getTime();
    if (!Number.isFinite(ts)) continue;
    const diffMs = Date.now() - ts;
    const diffMin = Math.max(0, Math.round(diffMs / 60000));
    const days = Math.floor(diffMin / 60 / 24);
    return {
      channel,
      at,
      daysAgo: days,
      label: _interventionLabel(channel, diffMin),
    };
  }
  return null;
};

const _interventionLabel = (channel, diffMin) => {
  const channelLabel =
    channel === "whatsapp" ? "WhatsApp chase" :
    channel === "phone"    ? "Phone call"     :
                             "Email chase";
  const when = _relTimeShort(diffMin);
  return `${channelLabel} · ${when}`;
};

const _relTimeShort = (diffMin) => {
  if (diffMin < 1)       return "just now";
  if (diffMin < 60)      return `${diffMin}m ago`;
  const h = Math.round(diffMin / 60);
  if (h < 24)            return `${h}h ago`;
  if (h < 48)            return "Yesterday";
  const d = Math.floor(h / 24);
  if (d < 7)             return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5)             return `${w}w ago`;
  const m = Math.floor(d / 30);
  return `${m}mo ago`;
};
